import { Directive, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { AbstractBraintreeButtonComponent } from '@df/module-braintree/abstract-braintree-button.component';
import { Braintree } from '@df/module-braintree/braintree.namespace';

@Directive()
export abstract class BraintreeApplePayComponentDef extends AbstractBraintreeButtonComponent<
    Braintree.IApplePayPaymentMethod,
    Braintree.IApplePayInstance
> {
    /**
     * Apple pay payment method available status
     * @type {Status}
     */
    applePayAvailableStatus = new Status();

    /**
     * Validate merchant status
     * @type {Status}
     */
    validateMerchantStatus = new Status();

    /**
     * Apple pay version required
     * @type {number}
     */
    protected readonly version = 3;

    /**
     * Not available message
     * @type {string}
     */
    protected readonly notAvailableMessage = 'Apple Pay is not available. Please select another payment method.';

    /**
     * Payment method code related to component
     * @type {string}
     */
    protected readonly methodCode = Braintree.Methods.ApplePay;

    protected applePayService = inject(ApplePayService);

    /**
     * Checks if we can use this component
     * @returns {boolean}
     */
    protected isAvailable(): boolean {
        return this.applePayService.isAvailable && this.applePayService.isSupportedVersion(this.version);
    }

    /**
     * on available error callback
     */
    protected override onAvailableError(): void {
        this.applePayAvailableStatus.error = this.notAvailableMessage;
    }

    //////////////////////////////////////////
    /////
    /////   Script methods
    /////
    //////////////////////////////////////////

    /**
     * Returns required scripts for braintree apple pay
     * @returns {string[]}
     */
    protected getJsAssets(): string[] {
        return [Braintree.Scripts.CLIENT, Braintree.Scripts.APPLE_PAY];
    }

    /**
     * Returns braintree button payment instance
     * @param clientInstance
     * @returns {Promise<Braintree.IApplePayInstance>}
     */
    protected getBraintreeButtonInstance(clientInstance: Braintree.IClientInstance): Promise<Braintree.IApplePayInstance> {
        return new Promise((resolve, reject) => {
            const applePay = this.braintreeService.getApplePay();
            if (applePay) {
                applePay.create({ client: clientInstance }).then(resolve).catch(reject);
            } else {
                reject();
            }
        });
    }

    /**
     * Checks if we can start adding button, every required scripts loaded etc
     * @returns {boolean}
     */
    protected canAddButton(): boolean {
        return !!(this.braintreeService.getClient() && this.braintreeService.getApplePay() && this.getClientToken());
    }

    /**
     * On add button error callback
     */
    protected onAddButtonError(): void {
        this.onScriptLoadError();
    }

    //////////////////////////////////////////
    /////
    /////   Button
    /////
    //////////////////////////////////////////

    /**
     * Adds apple pay button to DOM
     */
    protected override addButton(): void {
        const button = this.getButton();

        if (this.containerElement && button) {
            this.containerElement.appendChild(button);
            this.buttonElement = button;
        } else {
            this.onScriptLoadError();
        }
    }

    /**
     * Returns apple pay button
     * @returns {Element}
     */
    protected getButton(): HTMLButtonElement | undefined {
        if (typeof document === 'undefined') {
            return undefined;
        }
        const el = document.createElement('button');
        el.title = 'Pay with Apple Pay';
        el.className = this.getButtonClass();
        el.lang = this.getLocale();
        el.type = 'button';
        el.addEventListener('click', e => {
            this.onClick(e);
        });
        return el;
    }

    /**
     * Returns button css class
     * @returns {string}
     */
    protected getButtonClass(): string {
        return 'apple-pay-button';
    }

    /**
     * On apple pay button click
     * @param {MouseEvent} event
     */
    protected onClick(event: MouseEvent): void {
        event.preventDefault();

        if (this.allowClick()) {
            const paymentRequest = this.getPaymentRequest();
            if (window.ApplePaySession && paymentRequest && this.braintreeButtonInstance) {
                if (this.inProgressStatus.busy || this.checkoutStatus.busy) {
                    return;
                }
                this.inProgressStatus.busy = true;
                const applePaySessionInstance = new window.ApplePaySession(this.version, paymentRequest);

                this.addApplePaySessionInstanceEvents(applePaySessionInstance);

                applePaySessionInstance.begin();
            } else {
                this.onAvailableError();
            }
        } else {
            this.validateCheckout();
        }
    }

    /**
     * Returns if click is allowed
     * @returns {boolean}
     */
    protected allowClick(): boolean {
        return this.isCheckoutValid;
    }

    /**
     * Adds apple pay session instance events callbacks
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected addApplePaySessionInstanceEvents(applePaySessionInstance: ApplePay.ISessionInstance): void {
        applePaySessionInstance.onvalidatemerchant = validateMerchanEvent => {
            this.onValidateMerchant(validateMerchanEvent, applePaySessionInstance);
        };

        applePaySessionInstance.oncancel = () => {
            this.onCancel();
        };

        applePaySessionInstance.onpaymentauthorized = paymentAuthorizedEvent => {
            this.onPaymentAuthorized(paymentAuthorizedEvent, applePaySessionInstance);
        };
    }

    /**
     * On apple pay button validate merchant callback
     * @param {ApplePay.IValidateMerchantEvent} validateMerchanEvent
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected onValidateMerchant(
        validateMerchanEvent: ApplePay.IValidateMerchantEvent,
        applePaySessionInstance: ApplePay.ISessionInstance
    ): void {
        if (this.braintreeButtonInstance) {
            this.braintreeButtonInstance
                .performValidation({
                    validationURL: validateMerchanEvent.validationURL,
                    displayName: this.getMerchantName()
                })
                .then(merchantSession => {
                    applePaySessionInstance.completeMerchantValidation(merchantSession);
                })
                .catch(() => {
                    this.validateMerchantStatus.error = 'Apple Pay failed to load.';
                    this.clear();
                    applePaySessionInstance.abort();
                });
        }
    }

    /**
     * On apple pay button canel callback
     */
    protected onCancel(): void {
        this.clear();
    }

    /**
     * On apple pay button payment authorized callback
     * @param {ApplePay.IPaymentEvent} paymentAuthorizedEvent
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected onPaymentAuthorized(
        paymentAuthorizedEvent: ApplePay.IPaymentEvent,
        applePaySessionInstance: ApplePay.ISessionInstance
    ): void {
        const applePaySession = window.ApplePaySession as ApplePay.ISession;
        const completeWithFailure = () => {
            this.checkoutStatus.error = this.paymentErrorMessage;
            applePaySessionInstance.completePayment({ status: applePaySession.STATUS_FAILURE });
            this.clear();
        };

        if (this.braintreeButtonInstance) {
            this.braintreeButtonInstance
                .tokenize({ token: paymentAuthorizedEvent.payment.token })
                .then(payload => {
                    const submissionData = this.getSubmissionData(paymentAuthorizedEvent);
                    submissionData.paymentMethod = {
                        method: this.methodCode,
                        additional_data: {
                            payment_method_nonce: payload.nonce
                        }
                    };
                    this.checkoutFormComponent.submitWithData(
                        submissionData,
                        () => {
                            applePaySessionInstance.completePayment({ status: applePaySession.STATUS_SUCCESS });
                            this.clear();
                        },
                        () => {
                            applePaySessionInstance.completePayment({ status: applePaySession.STATUS_FAILURE });
                            this.clear();
                        },
                        true
                    );
                })
                .catch(() => {
                    completeWithFailure();
                });
        } else {
            completeWithFailure();
        }
    }

    /**
     * Returns checkout submission data
     * @param {ApplePay.IPaymentEvent} paymentAuthorizedEvent
     * @returns {Checkout.ISubmissionData}
     */
    protected getSubmissionData(paymentAuthorizedEvent: ApplePay.IPaymentEvent): Checkout.ISubmissionData {
        return this.checkoutFormComponent.getSubmissionData();
    }

    /**
     * Returns merchant name
     * @returns {string}
     */
    protected getMerchantName(): string {
        return this.method!.config.merchantName;
    }

    /**
     * Returns apple pay payment request
     * @returns {ApplePay.IPaymentRequest}
     */
    protected getPaymentRequest(): ApplePay.IPaymentRequest | undefined {
        const requestData = this.getPaymentRequestData();
        if (requestData && this.braintreeButtonInstance) {
            return this.braintreeButtonInstance.createPaymentRequest(requestData) as ApplePay.IPaymentRequest;
        }
        return undefined;
    }

    /**
     * Returns apple pay request data used to create apple pay payment request with braintree
     * @returns {Braintree.IApplePayPaymentRequest}
     */
    protected getPaymentRequestData(): Braintree.IApplePayPaymentRequest | undefined {
        const totalLine = this.getTotalLine();
        if (totalLine) {
            return {
                total: totalLine
            };
        }
        return undefined;
    }

    /**
     * Returns total for apple pay payment request
     * @param {ApplePay.ILineItem} type
     */
    protected getTotalLine(type = ApplePay.LineItemType.final): ApplePay.ILineItem | undefined {
        const total = this.getTotal();
        if (total) {
            return {
                amount: total.toString(),
                label: this.getMerchantName(),
                type
            };
        }
        return undefined;
    }

    /**
     * Returns locale for apple pay button
     * @returns {string}
     */
    protected getLocale(): string {
        const appLocale = this.app.data.locale;
        return appLocale || 'en_GB';
    }

    /**
     * Returns html element fo button
     * @returns {HTMLElement | undefined}
     */
    protected override getContainerElement(): HTMLElement | undefined {
        return this.container && this.container.nativeElement;
    }

    /**
     * Clear method called on apple pay close
     */
    protected clear(): void {
        this.inProgressStatus.busy = false;
    }
}
